import { AsyncPipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatError, MatFormField, MatHint } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable } from 'rxjs';
import { ENVIRONMENT } from '../../../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../../../api/api-types/api-response.api';
import { AppUpdateLek, Lek, LekCategories, LekSponsor } from '../../../../../api/app-types/lek.app';
import { DateRange } from '../../../../../api/app-types/validation.app';
import { LEK_PROVIDER } from '../../../../../api/services/lek.service';
import { VALIDATION_SERVICE } from '../../../../../api/services/validation.service';
import { mapEditFormFieldNameToEditableLekMetadataField } from '../../../../../api/transformers/lek.transform';
import { CUSTOM_DATE_FORMATS } from '../../../../../core/adapters/custom-date-format';
import { LvrDefaultActions } from '../../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../../core/directives/lvr-input.directive';
import { LOADING } from '../../../../../core/services/loading.service';
import { matDateProviders } from '../../../../../shared/helpers/date.func';
import { hasError } from '../../../../../shared/helpers/has-error.func';
import { LOGGER } from '../../../../../shared/logging/log.service';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../../shared/pipes/append-asterisk.pipe';
import { RouteInjection } from '../../../../../shared/routing/route-injection.class';
import { LvrAutoSaveDirective } from '../../../../../shared/storage/auto-save.directive';
import { FormPersistenceService } from '../../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../../shared/storage/session-manager.service';
import { endDateAfterStartDateValidator } from '../../../../../shared/validators/end-date-after-start-date.validator';
import { ImmediateErrorStateMatcher } from '../../../../../shared/validators/immediate-error-state-matcher';
import { InfoTooltip } from '../../../../../core/components/info-tooltip/info-tooltip';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-edit-lek',
	templateUrl: './edit-lek.html',
	styleUrl: './edit-lek.scss',
	imports: [
		AsyncPipe,
		LvrPaddingDirective,
		TranslocoPipe,
		ReactiveFormsModule,
		LvrField,
		MatFormField,
		LvrInputDirective,
		MatError,
		MatDatepickerModule,
		MatNativeDateModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		LvrDefaultActions,
		MatCheckbox,
		LvrAppendAsteriskPipe,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		MatHint,
		InfoTooltip,
	],
	providers: [matDateProviders],
})
export class EditLek extends RouteInjection {
	public readonly DATEFORMAT = CUSTOM_DATE_FORMATS.parse.dateInput;
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly logger = inject(LOGGER);
	private readonly loading = inject(LOADING);
	private readonly api = inject(LEK_PROVIDER);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly lekWithMetadata = this.resolveDataAsSignal<ApiResponse<Lek, Metadata>>('lekWithMetadata');
	public readonly lek = computed(() => this.lekWithMetadata()?.data);
	private readonly lekMetadata = computed(() => this.lekWithMetadata()?.metadata);
	public readonly editableCtrls = computed<string[]>(() => this.lekMetadata()?.editOnlyFields ?? []);
	private readonly editableControlPaths = [
		'startDate',
		'endDate',
		'performanceResultId',
		'result',
		'additionalFields.sponsorId',
		'additionalFields.isHandledByExternalSponsor',
		'duration',
	];

	public readonly assignmentId = this.resolveDataAsSignal<number>('assignmentId');
	//Get Assistance Categories
	public readonly performanceResult = this.resolveDataAsSignal<LekCategories>('lekCategories');
	public readonly performanceResultOptions = computed(
		() => this.performanceResult()?.categoryAssistanceResponses ?? [],
	);
	//Get Sponsor Categories
	private readonly sponsorPage = this.resolveDataAsSignal<PaginatedResponse<LekSponsor>>('sponsorPage');
	public readonly sponsors = computed(() => (this.sponsorPage()?.entities ?? []) as LekSponsor[]);

	public readonly env = inject(ENVIRONMENT);
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly hasError = hasError;
	public readonly draftId = computed(() => this.lek()?.id?.toString() ?? '');

	private readonly canEditRestricted = hasPermission(this.session.permissions, 'AssistanceResults.Edit.Restricted');
	private readonly canEditAll = hasPermission(this.session.permissions, 'AssistanceResults.Edit');

	public readonly disableEditLekButton = computed(() => {
		if (this.canEditAll()) return false;
		if (this.canEditRestricted()) return !(this.editableCtrls()?.length ?? 0 > 0);
		return true;
	});

	public readonly validLekDateRange = signal<DateRange | undefined>(undefined);
	public readonly validMinDate = computed(() => {
		return this.validLekDateRange()?.min;
	});

	public readonly validMaxDate = computed(() => {
		return this.validLekDateRange()?.max;
	});

	public readonly isLoading = this.loading.isCurrentlyLoading('lek-update-save', 'requestKey');

	private readonly fb = inject(FormBuilder);
	public readonly form = this.fb.group(
		{
			startDate: [this.lek()?.startDate, [Validators.required]],
			endDate: [this.lek()?.endDate],
			performanceResultId: this.fb.control<undefined | number>(this.lek()?.categoryAssistance.id, [
				Validators.required,
			]),
			result: [this.lek()?.result],
			additionalFields: this.fb.group({
				sponsorId: [this.lek()?.sponsor?.id],
				isHandledByExternalSponsor: [this.lek()?.isHandledByExternalSponsor ?? false],
			}),
			duration: this.fb.control<number | undefined>(this.lek()?.duration),
		},
		{
			validators: [endDateAfterStartDateValidator('startDate', 'endDate')],
		},
	);

	private readonly performanceResultId = toSignal(
		this.form.controls['performanceResultId'].valueChanges.pipe(map(() => this.form.get('performanceResultId')!.value)),
		{
			initialValue: this.lek()?.categoryAssistance.id,
		},
	);

	public readonly preSelectedInstitutionBranch = computed(() => {
		const branch = this.performanceResult()?.institutionBranch;
		const performanceResultId = this.performanceResultId();
		const performanceResult = this.performanceResultOptions().find((res) => res.id === performanceResultId);
		if (!branch) return;
		if (branch.schoolNumber && performanceResult?.requiredEmploymentType === 'S') return branch;
		if (!branch.schoolNumber && performanceResult?.requiredEmploymentType === 'J') return branch;
		return;
	});

	public readonly canEditDuration = computed(() => {
		const selected = this.selectedPerformanceResult();
		return selected?.canEnterDuration ?? false;
	});

	public get additionalFieldsGroup() {
		return this.form.get('additionalFields') as FormGroup;
	}

	public readonly selectedPerformanceResult = computed(() => {
		const id = this.performanceResultId();
		const option = this.performanceResultOptions().find((res) => res.id === id);
		return option;
	});

	public constructor() {
		super();
		//effect for autoselecting the branch option
		effect(() => {
			const branch = this.preSelectedInstitutionBranch();
			if (!branch) return;
			this.additionalFieldsGroup.get('workplaceId')?.setValue(branch.id, { emitEvent: false });
		});

		effect(() => {
			const canEdit = this.canEditDuration();
			const durationCtrl = this.form.get('duration');
			if (canEdit) {
				durationCtrl?.setValidators([Validators.required, Validators.min(1), Validators.max(3)]);
			} else {
				durationCtrl?.clearValidators();
				durationCtrl?.setValue(undefined);
			}
			durationCtrl?.updateValueAndValidity();
		});

		//effect to set sponsorId either with validator.required or without
		effect(() => {
			const selected = this.selectedPerformanceResult();
			const sponsorCtrl = this.additionalFieldsGroup.get('sponsorId');
			const externalSponsorCtrl = this.additionalFieldsGroup.get('isHandledByExternalSponsor');

			if (selected?.requiredSponsor === 'J') {
				sponsorCtrl?.setValidators([Validators.required]);
			} else if (selected?.requiredSponsor === 'S') {
				sponsorCtrl?.clearValidators();
			} else {
				sponsorCtrl?.clearValidators();
			}

			if (selected?.requiredSponsor !== 'T') {
				externalSponsorCtrl?.setValue(false, { emitEvent: false });
			}

			sponsorCtrl?.updateValueAndValidity();
		});

		// Valid Date Range
		effect(() => {
			const assignmentId = this.assignmentId();
			const lekId = this.lek()?.id;
			if (!assignmentId || !lekId) return;

			void this.loadValidLekDateRange(assignmentId, lekId);
		});

		// Apply metadata edit restrictions
		effect(() => {
			const meta = this.editableCtrls();

			this.editableControlPaths.forEach((path) => {
				const control = this.form.get(path);

				if (!control) {
					return;
				}

				if (meta.includes('*') || this.canEditControl(path)) {
					control.enable({ emitEvent: false });
				} else {
					control.disable({ emitEvent: false });
				}
			});
		});
	}

	private canEditControl(controlName: string): boolean {
		const meta = this.editableCtrls() ?? [];

		if (meta.includes('*')) {
			return true;
		}

		const metadataFieldName = mapEditFormFieldNameToEditableLekMetadataField(controlName).toLowerCase();

		return meta.some((field) => field.toLowerCase() === metadataFieldName);
	}

	public async save() {
		if (this.isLoading() || this.disableEditLekButton()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'lek-update-save');
		try {
			const rawValue = this.form.getRawValue();

			const body: AppUpdateLek = {
				startDate: rawValue.startDate!,
				endDate: rawValue.endDate!,
				result: rawValue.result!,
				categoryAssistanceId: rawValue.performanceResultId!,
				assignmentId: this.assignmentId()!,
			};

			const requiredSponsor = this.selectedPerformanceResult()?.requiredSponsor;
			const workplaceId = this.preSelectedInstitutionBranch()?.workplaceJobs?.[0]?.id;
			const isHandledByExternalSponsor =
				requiredSponsor === 'T' ? (rawValue.additionalFields?.isHandledByExternalSponsor ?? false) : false;

			if (requiredSponsor === 'S') {
				body.appUpdateLekDynamicFields = {
					isHandledByExternalSponsor,
					sponsorId: undefined,
					workplaceId: workplaceId ?? undefined,
				};
			} else if (requiredSponsor === 'J' || requiredSponsor === 'X') {
				body.appUpdateLekDynamicFields = {
					isHandledByExternalSponsor,
					sponsorId: rawValue.additionalFields?.sponsorId ?? undefined,
					workplaceId: workplaceId ?? undefined,
				};
			} else if (requiredSponsor === 'E') {
				body.appUpdateLekDynamicFields = {
					isHandledByExternalSponsor,
				};
			} else if (requiredSponsor === 'T') {
				body.appUpdateLekDynamicFields = {
					isHandledByExternalSponsor,
				};
			}

			if (this.canEditDuration()) {
				body.duration = rawValue.duration!;
			}

			await this.api.update(this.lek()!.id, body);
			await this.router.navigate(['..'], { relativeTo: this.route });
			this.draftService.deleteDraft('lek', this.draftId());
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.lek_update_success')),
			);
		} catch (e) {
			this.logger.error(e as Error);
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.lek_update_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async cancel() {
		this.draftService.deleteDraft('lek', this.draftId());
		await this.router.navigate(['..'], { relativeTo: this.route });
	}

	private async loadValidLekDateRange(assignmentId: number, lekId: number) {
		try {
			const range = await this.validationApi.getValidDateRangeForLEKOfAssignment(assignmentId, lekId);
			this.validLekDateRange.set(range);
		} catch (error) {
			console.error('Fehler beim Laden des gültigen Zeitraums für den LEK', error);
			this.validLekDateRange.set(undefined);
		}
	}

	public infoTextAriaLabel(description: string) {
		return `Info zu Leistung / Ergebnis ${description}`;
	}

	public hasHintText(optionId: number): Observable<boolean> {
		const key = this.getInfoTextKey(optionId);
		return this.transloco.selectTranslate(key).pipe(map((translated) => translated !== key));
	}

	public isLocalOrDevEnvironment(): boolean {
		const url = window.location.hostname;
		return url.includes('localhost') || url.includes('dev.');
	}

	public getInfoTextKey(optionId: number): string {
		if (this.isLocalOrDevEnvironment()) {
			return `lek.create.info_text_lek_local_development.${optionId}`;
		}
		return `lek.create.info_text_lek.${optionId}`;
	}
}
